import React, { useEffect, useState } from "react";
import { useProjects } from "../context/ProjectContext";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { NoProject } from "../components/EmptyState";
import CostCodeSelect from "../components/CostCodeSelect";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { nf } from "../lib/format";
import { Plus, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function DataEntry() {
  const { selected } = useProjects();
  const { can } = useAuth();
  const [codes, setCodes] = useState([]);
  const [changes, setChanges] = useState([]);
  const [actuals, setActuals] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    if (!selected) { setLoading(false); return; }
    setLoading(true);
    const [c, ch, a, w] = await Promise.all([
      api.get(`/projects/${selected.id}/cost-codes`),
      api.get(`/projects/${selected.id}/changes`),
      api.get(`/projects/${selected.id}/actuals`),
      api.get(`/projects/${selected.id}/warnings`),
    ]);
    setCodes(c.data); setChanges(ch.data); setActuals(a.data); setWarnings(w.data.warnings);
    setLoading(false);
  };
  useEffect(() => { reload(); }, [selected]);

  if (!selected) return <NoProject />;
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0B192C]" /></div>;

  return (
    <div className="space-y-4" data-testid="data-entry-page">
      <h1 className="font-head text-2xl font-bold text-slate-900">Data Entry</h1>

      {warnings.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3" data-testid="dq-warnings">
          <div className="flex items-center gap-2 text-blue-800 font-semibold text-sm mb-1">
            <AlertTriangle className="w-4 h-4" /> {warnings.length} data-quality warning(s)
          </div>
          <div className="text-xs text-blue-700 space-y-0.5 max-h-24 overflow-y-auto">
            {warnings.map((w, i) => <div key={i}><b>{w.type}:</b> {w.detail}</div>)}
          </div>
        </div>
      )}

      <Tabs defaultValue="codes">
        <TabsList data-testid="data-entry-tabs">
          <TabsTrigger value="codes" data-testid="tab-cost-codes">Cost Codes ({codes.length})</TabsTrigger>
          <TabsTrigger value="changes" data-testid="tab-changes">Changes ({changes.length})</TabsTrigger>
          <TabsTrigger value="actuals" data-testid="tab-actuals">Weekly Actuals ({actuals.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="codes"><CostCodesTab pid={selected.id} codes={codes} reload={reload} canEdit={can.manage} /></TabsContent>
        <TabsContent value="changes"><ChangesTab pid={selected.id} codes={codes} changes={changes} reload={reload} canEdit={can.manage} /></TabsContent>
        <TabsContent value="actuals"><ActualsTab pid={selected.id} codes={codes} actuals={actuals} reload={reload} canEdit={can.field} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------- Cost Codes ---------- */
function CostCodesTab({ pid, codes, reload, canEdit }) {
  const empty = { cost_code: "", description: "", uom: "", labor_class: "", standard_labor_rate: "", original_budget_qty: "", original_budget_hours: "" };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${pid}/cost-codes`, {
        ...form,
        standard_labor_rate: parseFloat(form.standard_labor_rate) || null,
        original_budget_qty: parseFloat(form.original_budget_qty) || 0,
        original_budget_hours: parseFloat(form.original_budget_hours) || 0,
      });
      toast.success("Cost code added");
      setOpen(false); setForm(empty); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/projects/${pid}/cost-codes/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <Toolbar title="Cost Codes" canEdit={canEdit} open={open} setOpen={setOpen} testid="add-cost-code" addLabel="Add Cost Code">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Cost Code *"><Input value={form.cost_code} onChange={(e) => setForm({ ...form, cost_code: e.target.value })} placeholder="09-913000" data-testid="cc-code-input" /></Field>
          <Field label="Description"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cc-desc-input" /></Field>
          <Field label="UOM"><Input value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} placeholder="LF / SF / EA" data-testid="cc-uom-input" /></Field>
          <Field label="Labor Class"><Input value={form.labor_class} onChange={(e) => setForm({ ...form, labor_class: e.target.value })} data-testid="cc-class-input" /></Field>
          <Field label="Standard $/hr"><Input type="number" value={form.standard_labor_rate} onChange={(e) => setForm({ ...form, standard_labor_rate: e.target.value })} data-testid="cc-rate-input" /></Field>
          <Field label="Original Budget Qty"><Input type="number" value={form.original_budget_qty} onChange={(e) => setForm({ ...form, original_budget_qty: e.target.value })} data-testid="cc-qty-input" /></Field>
          <Field label="Original Budget Hours"><Input type="number" value={form.original_budget_hours} onChange={(e) => setForm({ ...form, original_budget_hours: e.target.value })} data-testid="cc-hours-input" /></Field>
        </div>
        <DialogFooter className="mt-4"><Button onClick={save} disabled={saving || !form.cost_code} className="bg-[#0B192C]" data-testid="cc-save-button">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button></DialogFooter>
      </Toolbar>
      <SimpleTable
        head={["Code", "Description", "UOM", "Class", "$/hr", "Orig Qty", "Orig Hrs", ""]}
        rows={codes} testid="cost-codes-table"
        render={(c) => [
          <span className="font-mono font-medium">{c.cost_code}</span>, c.description, c.uom, c.labor_class,
          c.standard_labor_rate ? `$${nf(c.standard_labor_rate, 0)}` : "—",
          nf(c.original_budget_qty, 0), nf(c.original_budget_hours),
          canEdit && <DeleteBtn onConfirm={() => del(c.id)} name={c.cost_code} testid={`del-cc-${c.cost_code}`} />,
        ]} />
    </div>
  );
}

/* ---------- Changes ---------- */
function ChangesTab({ pid, codes, changes, reload, canEdit }) {
  const empty = { date: "", change_id: "", cost_code: "", description: "", status: "Pending", qty_adjustment: "", hour_adjustment: "", include_in_forecast: true, notes: "" };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${pid}/changes`, {
        ...form, qty_adjustment: parseFloat(form.qty_adjustment) || 0,
        hour_adjustment: parseFloat(form.hour_adjustment) || 0,
      });
      toast.success("Change added"); setOpen(false); setForm(empty); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/projects/${pid}/changes/${id}`); toast.success("Deleted"); reload(); };
  const toggle = async (c) => {
    await api.patch(`/projects/${pid}/changes/${c.id}`, { ...c, include_in_forecast: !c.include_in_forecast });
    reload();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <Toolbar title="Changes" canEdit={canEdit} open={open} setOpen={setOpen} testid="add-change" addLabel="Add Change">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="ch-date-input" /></Field>
          <Field label="Change ID"><Input value={form.change_id} onChange={(e) => setForm({ ...form, change_id: e.target.value })} placeholder="CO-001" data-testid="ch-id-input" /></Field>
          <Field label="Cost Code *"><CostCodeSelect codes={codes} value={form.cost_code} onChange={(v) => setForm({ ...form, cost_code: v })} testid="ch-code-select" /></Field>
          <Field label="Status"><Input value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="ch-status-input" /></Field>
          <Field label="Qty Change"><Input type="number" value={form.qty_adjustment} onChange={(e) => setForm({ ...form, qty_adjustment: e.target.value })} data-testid="ch-qty-input" /></Field>
          <Field label="Hours Change"><Input type="number" value={form.hour_adjustment} onChange={(e) => setForm({ ...form, hour_adjustment: e.target.value })} data-testid="ch-hours-input" /></Field>
          <Field label="Description" full><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ch-desc-input" /></Field>
          <div className="col-span-2 flex items-center gap-2">
            <Switch checked={form.include_in_forecast} onCheckedChange={(v) => setForm({ ...form, include_in_forecast: v })} data-testid="ch-include-switch" />
            <Label>Include in working forecast</Label>
          </div>
        </div>
        <DialogFooter className="mt-4"><Button onClick={save} disabled={saving || !form.cost_code} className="bg-[#0B192C]" data-testid="ch-save-button">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button></DialogFooter>
      </Toolbar>
      <SimpleTable
        head={["Date", "ID", "Cost Code", "Description", "Qty", "Hrs", "In Forecast", ""]}
        rows={changes} testid="changes-table"
        render={(c) => [
          c.date, c.change_id, <span className="font-mono">{c.cost_code}</span>, c.description,
          nf(c.qty_adjustment, 0), nf(c.hour_adjustment),
          <Switch checked={c.include_in_forecast} disabled={!canEdit} onCheckedChange={() => toggle(c)} data-testid={`ch-toggle-${c.id}`} />,
          canEdit && <DeleteBtn onConfirm={() => del(c.id)} name={c.change_id} testid={`del-ch-${c.id}`} />,
        ]} />
    </div>
  );
}

/* ---------- Weekly Actuals ---------- */
function ActualsTab({ pid, codes, actuals, reload, canEdit }) {
  const empty = { week_ending: "", cost_code: "", installed_qty: "", st_hours: "", ot_hours: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${pid}/actuals`, {
        ...form, installed_qty: parseFloat(form.installed_qty) || 0,
        st_hours: parseFloat(form.st_hours) || 0, ot_hours: parseFloat(form.ot_hours) || 0,
      });
      toast.success("Weekly entry added"); setOpen(false); setForm(empty); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/projects/${pid}/actuals/${id}`); toast.success("Deleted"); reload(); };

  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="px-3 pt-2 text-xs text-amber-700">
        Enter <b>incremental</b> installed quantity for each period — do not paste cumulative-to-date totals.
      </div>
      <Toolbar title="Weekly Actuals" canEdit={canEdit} open={open} setOpen={setOpen} testid="add-actual" addLabel="Add Weekly Entry">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Week Ending *"><Input type="date" value={form.week_ending} onChange={(e) => setForm({ ...form, week_ending: e.target.value })} data-testid="ac-week-input" /></Field>
          <Field label="Cost Code *"><CostCodeSelect codes={codes} value={form.cost_code} onChange={(v) => setForm({ ...form, cost_code: v })} testid="ac-code-select" /></Field>
          <Field label="Installed Qty (this period)"><Input type="number" value={form.installed_qty} onChange={(e) => setForm({ ...form, installed_qty: e.target.value })} data-testid="ac-qty-input" /></Field>
          <Field label="Straight-Time Hours"><Input type="number" value={form.st_hours} onChange={(e) => setForm({ ...form, st_hours: e.target.value })} data-testid="ac-st-input" /></Field>
          <Field label="Overtime Hours"><Input type="number" value={form.ot_hours} onChange={(e) => setForm({ ...form, ot_hours: e.target.value })} data-testid="ac-ot-input" /></Field>
          <Field label="Notes" full><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="ac-notes-input" /></Field>
        </div>
        <DialogFooter className="mt-4"><Button onClick={save} disabled={saving || !form.cost_code || !form.week_ending} className="bg-[#0B192C]" data-testid="ac-save-button">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button></DialogFooter>
      </Toolbar>
      <SimpleTable
        head={["Week", "Cost Code", "Installed", "ST", "OT", "Source", ""]}
        rows={actuals} testid="actuals-table"
        render={(a) => [
          a.week_ending, <span className="font-mono">{a.cost_code}</span>, nf(a.installed_qty, 0),
          nf(a.st_hours), nf(a.ot_hours), a.source,
          canEdit && <DeleteBtn onConfirm={() => del(a.id)} name={`${a.week_ending} ${a.cost_code}`} testid={`del-ac-${a.id}`} />,
        ]} />
    </div>
  );
}

/* ---------- Shared bits ---------- */
const Field = ({ label, children, full }) => (
  <div className={full ? "col-span-2" : ""}><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>
);

function Toolbar({ title, canEdit, open, setOpen, children, testid, addLabel }) {
  return (
    <div className="flex items-center justify-between p-3 border-b border-slate-100">
      <h3 className="font-head font-semibold text-slate-800">{title}</h3>
      {canEdit && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#E8791A] hover:bg-[#D66A0F] text-white" data-testid={testid}>
              <Plus className="w-4 h-4 mr-1" /> {addLabel}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg"><DialogHeader><DialogTitle className="font-head">{addLabel}</DialogTitle></DialogHeader>{children}</DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SimpleTable({ head, rows, render, testid }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="bg-slate-50 text-xs text-slate-600 uppercase tracking-wide">
          {head.map((h, i) => <th key={i} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} className="px-3 py-8 text-center text-slate-400">No records yet.</td></tr>
          ) : rows.map((r, ri) => (
            <tr key={r.id || ri} className="border-t border-slate-100 hover:bg-slate-50">
              {render(r).map((cell, ci) => <td key={ci} className="px-3 py-2 whitespace-nowrap">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeleteBtn({ onConfirm, name, testid }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700 h-7 w-7 p-0" data-testid={testid}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this record?</AlertDialogTitle>
          <AlertDialogDescription>This will permanently delete <b>{name}</b>. This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
